/* ---------------------------------------------------------------------------
   The handover: construction ends, and the bridge goes into service.

   LIFECYCLE.md `LC-11`; the table is migrations/0040. Shared by the API and the
   screens, so the fields a form offers are the fields the endpoint reads, and
   the refusal a button's title warns of is the refusal the endpoint makes.

   TWO PEOPLE, NEVER ONE (§2, decision 2). A resident engineer
   (`handover.prepare`) prepares it on the site record; an approver
   (`handover.accept`) accepts it; and whoever prepared it cannot accept it,
   whatever roles they hold.

   WHAT IT RECORDS: the completion (taking-over) date — the handover date the
   inspection cycle counts from and the baseline inspection is due on — the
   defects-liability period and its end, the as-built model, the inventory
   values read from that model, the open NCRs and incidents carried over as
   defects, and a waiver with a reason for each hold point or NCR still open.

   A VALUE'S SOURCE IS DERIVED, NEVER TYPED. The server reads the as-built
   model's export on every save and marks each inventory value `model` where
   it is the model's and `edited` where somebody changed it — the same rule as
   a test result's pass (src/data/siteQuality.ts).
   --------------------------------------------------------------------------- */

import type { WorksKind } from './phase.ts';

export const HANDOVER_STATUSES = ['draft', 'prepared', 'accepted'] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number];

export const HANDOVER_STATUS_LABEL: Readonly<Record<HandoverStatus, string>> = {
  draft: 'Draft',
  prepared: 'Prepared',
  accepted: 'Accepted',
};

export type HandoverFieldKey =
  | 'spans'
  | 'length'
  | 'spanLength'
  | 'deckWidth'
  | 'girders'
  | 'bearings'
  | 'abutmentFoundation'
  | 'pierFoundation';

export interface HandoverField {
  key: HandoverFieldKey;
  label: string;
  kind: 'int' | 'num' | 'text';
  unit?: string;
  min?: number;
  max?: number;
  /**
   * Whether acceptance writes it to the inventory record. The girder count has
   * no column on `bridges` (`TN-20` keeps that table's shape fixed), so it
   * stays on the handover and the record reads it from there.
   */
  inventory: boolean;
}

/** The inventory the as-built model can fill, in the order the form lists it. Bounds as bridgeEdit.ts. */
export const HANDOVER_FIELDS: readonly HandoverField[] = [
  { key: 'spans', label: 'Spans', kind: 'int', min: 1, max: 50, inventory: true },
  { key: 'length', label: 'Total length', kind: 'num', unit: 'm', min: 0, max: 5000, inventory: true },
  { key: 'spanLength', label: 'Span length', kind: 'num', unit: 'm', min: 0, max: 500, inventory: true },
  { key: 'deckWidth', label: 'Deck width', kind: 'num', unit: 'm', min: 0, max: 100, inventory: true },
  { key: 'girders', label: 'Girders', kind: 'int', min: 1, max: 100, inventory: false },
  { key: 'bearings', label: 'Bearings', kind: 'text', max: 100, inventory: true },
  { key: 'abutmentFoundation', label: 'Abutment foundation', kind: 'text', max: 100, inventory: true },
  { key: 'pierFoundation', label: 'Pier foundation', kind: 'text', max: 100, inventory: true },
];

export type FieldValue = number | string | null;

/** One inventory value on the handover, and where it came from. */
export interface HandoverValue {
  value: FieldValue;
  /** `model` where it is the as-built model's value, `edited` where somebody changed or supplied it. */
  source: 'model' | 'edited';
  /** What the model said, where it said anything. */
  modelValue: FieldValue;
}

/** An open NCR or incident, carried over as a defect on acceptance. */
export interface HandoverCarry {
  kind: 'ncr' | 'incident';
  /** The site item's or incident's id. */
  id: string;
  /** NCR-002, or "Incident 2026-08-14" — what the defect names as its origin. */
  ref: string;
  title: string;
  /** The defect category and severity the resident engineer gave it. */
  code: string | null;
  severity: string | null;
  /** The defect it became, once accepted. */
  defectId: string | null;
}

/** A recorded reason for accepting with a hold point or NCR still open. */
export interface HandoverWaiver {
  itemId: string;
  ref: string;
  reason: string;
  by: string | null;
  at: string;
}

export interface Handover {
  id: string;
  bridgeId: string;
  /** A new structure's handover, or the completion of works on one in service. Absent: a handover. */
  kind?: HandoverKind;
  /** The works being completed, on a `works` completion. */
  worksKind?: WorksKind | null;
  status: HandoverStatus;
  completedOn: string | null;
  dlpMonths: number | null;
  dlpEndsOn: string | null;
  modelId: string | null;
  /** The revision of that model the inventory was read from (migration 0048). */
  modelRevision?: string | null;
  inventory: Partial<Record<HandoverFieldKey, HandoverValue>>;
  /** What the model could not answer, one sentence each. */
  modelNotes: string[];
  carry: HandoverCarry[];
  waivers: HandoverWaiver[];
  note: string;
  preparedBy: string | null;
  preparedAt: string | null;
  acceptedBy: string | null;
  acceptedAt: string | null;
  /** The baseline inspection's due day: the handover date (LC-60). */
  baselineDueOn: string | null;
  updatedAt: string;
}

/**
 * A handover's kind (migration 0045). `handover` takes a new structure into
 * service, once; `works` completes works on a bridge already in service
 * (LC-82) and leaves the phase where it is.
 */
export type HandoverKind = 'handover' | 'works';

/** What the bridge record carries of an accepted handover. */
export interface HandoverSummary {
  id: string;
  completedOn: string;
  dlpEndsOn: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  modelId: string | null;
  /** The inventory fields accepted as the model's own values. */
  modelFields: HandoverFieldKey[];
  /** The fields accepted with a value somebody entered in the model's place. */
  enteredFields: HandoverFieldKey[];
  girders: number | null;
}

/** What the bridge record carries of its latest accepted works completion (LC-82). */
export interface WorksCompletionSummary extends HandoverSummary {
  worksKind: WorksKind | null;
}

export function isHandoverField(key: unknown): key is HandoverFieldKey {
  return typeof key === 'string' && HANDOVER_FIELDS.some((f) => f.key === key);
}

/** The end of the defects-liability period: the completion date plus its months. */
export function dlpEnd(completedOn: string, months: number): string {
  const d = new Date(`${completedOn}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  /* The same day of the month, or its last day where the month is shorter. */
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

/** Whether a value is the model's, compared as the form shows it. */
export function sameValue(a: FieldValue, b: FieldValue): boolean {
  if (a === null || b === null) return a === b;
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-6;
  return String(a).trim() === String(b).trim();
}

/** The blocking items no waiver answers — what refuses the acceptance. */
export function unwaived<T extends { id: string; ref: string }>(
  blocking: readonly T[],
  waivers: readonly Pick<HandoverWaiver, 'itemId' | 'reason'>[],
): T[] {
  const waived = new Set(waivers.filter((w) => w.reason.trim() !== '').map((w) => w.itemId));
  return blocking.filter((b) => !waived.has(b.id));
}

/** The carried items with no category or severity yet — what refuses the preparation. */
export function unclassified(carry: readonly HandoverCarry[]): HandoverCarry[] {
  return carry.filter((c) => !c.code || !c.severity);
}
